import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Zap, Heart, Eye, BadgeCheck, EyeOff, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CATEGORIES, AVATAR_GRADIENTS } from "@/lib/categories";
import { getUser } from "@/lib/user";
import { versionMedia } from "@/lib/apercu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { BadgeNouveau } from "@/lib/annonces";
import { BadgesMedias } from "@/components/BadgesMedias";
import { ApercuVideoSurvol } from "@/components/ApercuVideoSurvol";

export const ProfileCard = ({ profile, index = 0, onVoir, lienAnnonce = null, testid = null, nouveau = false }) => {
  const cat = CATEGORIES[profile.category];
  const gradient = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
  const navigate = useNavigate();
  const [likes, setLikes] = useState(profile.likes || 0);
  const [liked, setLiked] = useState(profile.liked || false);
  const [likeAnim, setLikeAnim] = useState(false);
  const [match, setMatch] = useState(false);
  const [survol, setSurvol] = useState(false);

  const sendLike = async () => {
    const user = getUser();
    if (!user) {
      toast.error("Créez votre profil pour aimer un membre.");
      navigate("/inscription");
      return;
    }
    if (user.id === profile.id) {
      toast.error("Vous ne pouvez pas liker votre propre profil.");
      return;
    }
    try {
      const res = await axios.post(`${API}/profiles/${profile.id}/like`, { visitor_id: user.id });
      setLikes(res.data.likeCount);
      setLiked(true);
      setLikeAnim(true);
      setTimeout(() => setLikeAnim(false), 550);
      if (navigator.vibrate) navigator.vibrate(40);
      if (res.data.alreadyLiked) {
        toast.info(`Vous aimez déjà ${profile.firstname}.`);
      } else if (res.data.mutual) {
        if (navigator.vibrate) navigator.vibrate([90, 50, 90]);
        setMatch(true);
        setTimeout(() => setMatch(false), 2800);
      } else {
        toast.success(`Vous aimez ${profile.firstname} !`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const sendFlash = async () => {
    const user = getUser();
    if (!user) {
      toast.error("Créez votre profil pour envoyer un Flash.");
      navigate("/inscription");
      return;
    }
    if (user.id === profile.id) {
      toast.error("C'est votre propre profil !");
      return;
    }
    try {
      const conv = await axios.post(`${API}/conversations`, { from_id: user.id, to_id: profile.id });
      if (conv.data.created) {
        await axios.post(`${API}/conversations/${conv.data.id}/messages`, {
          sender_id: user.id,
          text: `⚡ Flash ! ${user.firstname} s'intéresse à votre profil.`,
        });
      }
      toast.success(`Flash envoyé à ${profile.firstname} !`, { description: "La conversation est ouverte." });
      navigate(`/messages?conv=${conv.data.id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("La messagerie est réservée aux abonnés.");
        navigate("/abonnement");
      } else {
        toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du Flash.");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="group bg-white rounded-2xl border border-[#E5DFD5] overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      data-testid={testid || `profile-card-${profile.id}`}
    >
      <div
        className="relative h-52 flex items-center justify-center overflow-hidden"
        style={{ background: gradient }}
        onMouseEnter={() => setSurvol(true)}
        onMouseLeave={() => setSurvol(false)}
      >
        {profile.photo ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${profile.photo}?v=${versionMedia()}${getUser() ? `&viewer_id=${getUser().id}` : ""}`}
            alt={profile.firstname}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        ) : (
          <>
            <span className="font-heading text-7xl font-extrabold text-white/90 select-none">
              {profile.firstname.charAt(0)}
            </span>
            <span className="absolute top-3 left-3 text-[10px] font-semibold tracking-wider uppercase bg-black/30 backdrop-blur-sm text-white px-2.5 py-1 rounded-full">
              Photo à venir
            </span>
          </>
        )}
        <span
          className="absolute bottom-3 right-3 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full text-white"
          style={{ backgroundColor: cat?.color }}
        >
          {cat?.name}
        </span>
        {profile.blind_date && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase bg-[#9f2558] text-white px-2.5 py-1 rounded-full" data-testid={`badge-blind-date-${profile.id}`}>
            <EyeOff size={10} /> Blind Date
          </span>
        )}
        <BadgesMedias
          nbPhotos={profile.nb_photos || 0}
          aVideo={Boolean(profile.a_video)}
          decale={Boolean(profile.blind_date)}
          testid={`annonce-medias-${profile.annonce_id || profile.id}`}
        />
        {profile.a_video && profile.annonce_id && (
          <ApercuVideoSurvol
            annonceId={profile.annonce_id}
            actif={survol}
            testid={`apercu-survol-${profile.annonce_id}`}
          />
        )}
      </div>
      <div className="p-5 space-y-3">
        <div className="flex items-baseline justify-between">
          <h3 className="font-heading text-lg font-bold text-stone-900 flex items-center gap-1.5">
            {profile.firstname}{profile.age ? `, ${profile.age}` : ""}
            {profile.verified && <BadgeCheck size={16} className="text-[#1D9BF0]" data-testid={`badge-verifie-${profile.id}`} />}
            {profile.certifie_visage && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#0A5C36]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0A5C36]"
                data-testid={`badge-certifie-${profile.id}`}>
                <ShieldCheck size={10} /> Certifié
              </span>
            )}
          </h3>
          <span className="flex items-center gap-1 text-xs text-stone-500">
            <MapPin size={12} /> {profile.city}{profile.quartier ? ` · ${profile.quartier}` : ""}
          </span>
        </div>
        {nouveau && <BadgeNouveau testid={`badge-nouveau-${profile.annonce_id || profile.id}`} />}
        <p className="text-sm text-stone-600 leading-relaxed line-clamp-3">{profile.description}</p>
        {lienAnnonce && (
          <a
            href={lienAnnonce}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`lire-annonce-${profile.annonce_id}`}
            className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-full text-white transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: cat?.color }}
          >
            Lire l'annonce complète
          </a>
        )}
        {onVoir && (
          <button
            onClick={() => onVoir(profile)}
            data-testid={`voir-profil-btn-${profile.id}`}
            className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: cat?.color }}
          >
            <Eye size={15} /> Voir le profil
          </button>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={sendLike}
            data-testid={`like-btn-${profile.id}`}
            className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-full border-2 transition-all duration-200 ${
              liked ? "border-[#E05638] bg-[#E05638]/10 text-[#E05638]" : "border-[#E5DFD5] text-stone-500 hover:border-[#E05638] hover:text-[#E05638]"
            }`}
          >
            <Heart size={15} fill={liked ? "#E05638" : "none"} className={likeAnim ? "like-pop" : ""} /> {likes}
          </button>
          <button
            onClick={sendFlash}
            data-testid={`flash-btn-${profile.id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full border-2 transition-all duration-200 hover:text-white"
            style={{ borderColor: cat?.color, color: cat?.color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = cat?.color)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <Zap size={15} /> Envoyer un Flash
          </button>
        </div>
      </div>
      {match && (
        <div className="match-overlay fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="match-overlay">
          <div className="match-card bg-white rounded-3xl px-10 py-9 text-center relative overflow-hidden">
            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-8 pointer-events-none">
              <Heart className="match-heart text-[#E05638]" fill="#E05638" size={22} />
              <Heart className="match-heart text-[#D4AF37]" fill="#D4AF37" size={28} />
              <Heart className="match-heart text-[#0A5C36]" fill="#0A5C36" size={20} />
            </div>
            <Heart size={52} className="mx-auto text-[#E05638]" fill="#E05638" />
            <h3 className="font-heading text-2xl font-extrabold text-stone-900 mt-3">C'est un match !</h3>
            <p className="text-sm text-stone-600 mt-1.5">{profile.firstname} vous aime aussi 💘</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};
